import React from 'react';
import { CircularProgress } from '@mui/material';
import { cn } from '../../utils/cn';

/**
 * Single source of truth for every call-to-action in the app. Before this, the same action
 * ("+ Raport", "Programează", "Adaugă") was filled in one card, outlined in another and an
 * underlined link in a third, with six different paddings between them.
 *
 * The icon is passed as a component rather than as a rendered node on purpose: it is the only
 * way to guarantee that every CTA icon lands at the same size and never drifts per call site.
 */

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'dangerGhost'
  | 'accent'
  | 'link';

/** `xs` exists only for dense popovers and chips — prefer `sm`/`md` for real page CTAs. */
export type ButtonSize = 'xs' | 'sm' | 'md';

/** Heroicons and MUI SVG icons both satisfy this. */
export type ButtonIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const BASE =
  'inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium transition active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1';

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white shadow-sm hover:bg-brand-700',
  secondary: 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
  ghost: 'text-slate-500 hover:bg-slate-100 hover:text-slate-900',
  danger: 'bg-red-600 text-white shadow-sm hover:bg-red-700',
  dangerGhost: 'text-red-600 hover:bg-red-50',
  /** Tinted brand outline — for secondary actions that still belong to the brand flow. */
  accent: 'border border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100',
  link: 'text-brand-600 hover:text-brand-700 hover:underline',
};

const SIZE: Record<ButtonSize, string> = {
  xs: 'gap-1 px-2.5 py-1 text-xs',
  sm: 'gap-1.5 px-3 py-2 text-xs',
  md: 'gap-2 px-4 py-2.5 text-sm',
};

const ICON = 'h-4 w-4 shrink-0';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ButtonIcon;
  /** Swaps the icon for a spinner and blocks input. */
  loading?: boolean;
  fullWidth?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    icon: Icon,
    loading = false,
    fullWidth = false,
    className,
    type = 'button',
    disabled,
    children,
    ...rest
  },
  ref
) {
  return (
    <button
      ref={ref}
      // eslint-disable-next-line react/button-has-type
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(BASE, VARIANT[variant], SIZE[size], fullWidth && 'w-full', className)}
      {...rest}
    >
      {loading ? (
        <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} aria-hidden="true" />
      ) : Icon ? (
        <Icon className={ICON} aria-hidden="true" />
      ) : null}
      {children}
    </button>
  );
});

export default Button;

export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: ButtonIcon;
  /** Required: an icon-only control is unusable by screen readers without it. */
  label: string;
  tone?: 'neutral' | 'danger';
  /** `md` is only for close buttons and menu triggers, where the tap target matters. */
  size?: 'sm' | 'md';
}

/**
 * Icon-only control: inline row actions (edit, delete), card close buttons, menu triggers.
 *
 * Every edit and delete affordance in the app renders through this, so they share one box size
 * and one hover treatment. Neutral actions warm to the brand gold; destructive ones go red.
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    { icon: Icon, label, tone = 'neutral', size = 'sm', className, ...rest },
    ref
  ) {
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        title={label}
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-md transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
          size === 'md' ? 'h-9 w-9' : 'h-7 w-7',
          tone === 'danger'
            ? 'text-slate-400 hover:bg-red-50 hover:text-red-600 active:text-red-700'
            : 'text-slate-400 hover:bg-slate-100 hover:text-brand-700 active:text-brand-800',
          // A `disabled` icon button otherwise looks exactly as clickable as a live one — same
          // color, same hover — which is what let a permission-blocked edit icon pass as active.
          'disabled:pointer-events-none disabled:opacity-40 disabled:active:scale-100',
          className
        )}
        {...rest}
      >
        <Icon className={size === 'md' ? 'h-5 w-5' : 'h-4 w-4'} aria-hidden="true" />
      </button>
    );
  }
);
